import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft, Library, Loader2, Trash2, FileText, Award, Plus, Eye, X, Calendar,
  Target, Sparkles, Mail, Linkedin, MessageSquare, Building2, Rocket, Code2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ACCENT = "#22D3EE";
const ACCENT_DEEP = "#0891B2";

function authHeaders() {
  try {
    const raw = sessionStorage.getItem("user_data");
    if (!raw) return {};
    const d = JSON.parse(raw);
    return d?.session_token ? { Authorization: `Bearer ${d.session_token}` } : {};
  } catch { return {}; }
}
function isLoggedIn() {
  try { return !!JSON.parse(sessionStorage.getItem("user_data") || "{}").session_token; }
  catch { return false; }
}

const ARTIFACT_META = [
  { key: "has_score",    Icon: Target,         label: "ATS score" },
  { key: "has_rewrite",  Icon: Sparkles,       label: "Rewrite" },
  { key: "has_cover",    Icon: Mail,           label: "Cover letter" },
  { key: "has_linkedin", Icon: Linkedin,       label: "LinkedIn" },
  { key: "has_dms",      Icon: MessageSquare,  label: "Recruiter DMs" },
];

export default function ResumeLibraryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTab, setPreviewTab] = useState("rewrite"); // rewrite | cover | linkedin | dms | score
  const [deletingId, setDeletingId] = useState(null);
  const [previewRecs, setPreviewRecs] = useState(null);
  const [previewRecsLoading, setPreviewRecsLoading] = useState(false);

  async function load() {
    setLoading(true); setErr("");
    if (!isLoggedIn()) {
      setErr("Please log in to view your saved applications.");
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/resume-ats/library`, { headers: authHeaders() });
      setItems(res.data.items || []);
    } catch (e) {
      setErr(e?.response?.status === 401 ? "Session expired. Please log in again." : "Could not load your applications.");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  // Phase 3 — fetch Python Lab recs whenever a preview opens that has JD-match data
  useEffect(() => {
    setPreviewRecs(null);
    const jd = preview?.score?.semantic?.jd_match;
    if (!jd) return;
    const missing = jd.missing_keywords || [];
    const gaps = jd.skill_gaps || [];
    if (missing.length + gaps.length === 0) return;
    setPreviewRecsLoading(true);
    axios.post(`${API}/resume-ats/skill-gap-recommend`, {
      missing_keywords: missing,
      skill_gaps: gaps,
      target_role: preview?.target_role || preview?.role || null,
      limit: 3,
    }, { timeout: 20_000 })
      .then(r => setPreviewRecs(r.data))
      .catch(() => setPreviewRecs(null))
      .finally(() => setPreviewRecsLoading(false));
  }, [preview?.package_id]);

  async function openPreview(packageId) {
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/resume-ats/library/${packageId}`, { headers: authHeaders() });
      setPreview(res.data);
      // Default tab = first artifact present
      const order = [
        { key: "rewritten_markdown",     tab: "rewrite" },
        { key: "cover_letter_markdown",  tab: "cover" },
        { key: "linkedin",               tab: "linkedin" },
        { key: "recruiter_dms",          tab: "dms" },
        { key: "score",                  tab: "score" },
      ];
      const first = order.find(o => res.data[o.key]) || order[0];
      setPreviewTab(first.tab);
    } catch { setErr("Could not open application."); }
    finally { setPreviewLoading(false); }
  }

  async function deletePackage(id) {
    if (!window.confirm("Delete this saved application permanently? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/resume-ats/library/${id}`, { headers: authHeaders() });
      setItems(prev => prev.filter(x => x.package_id !== id));
      if (preview?.package_id === id) setPreview(null);
    } catch { setErr("Could not delete application."); }
    finally { setDeletingId(null); }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-zinc-800/60 bg-zinc-950/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/ai-tools/resume-ats" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm" data-testid="back-to-resume">
            <ArrowLeft size={16} /> ShansoResume
          </Link>
          <div className="flex items-center gap-2" style={{ color: ACCENT }}>
            <Award size={18} />
            <span className="font-semibold tracking-tight text-sm">My Applications</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
              <Library size={28} style={{ color: ACCENT }} /> Application Packages
            </h1>
            <p className="text-zinc-400 text-sm mt-2">
              {loading ? "Loading…" : `${items.length} saved application${items.length === 1 ? "" : "s"}`}
            </p>
          </div>
          <button onClick={() => navigate("/ai-tools/resume-ats")}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-black font-bold text-sm"
                  style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                  data-testid="new-package-btn">
            <Plus size={14} /> New application
          </button>
        </div>

        {err && <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm p-3 rounded-lg mb-6" data-testid="library-error">{err}</div>}

        {loading ? (
          <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
        ) : items.length === 0 && !err ? (
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-10 text-center" data-testid="empty-library">
            <FileText size={40} className="mx-auto text-zinc-700 mb-3" />
            <p className="text-zinc-300 text-base font-semibold mb-1">No applications saved yet</p>
            <p className="text-zinc-500 text-sm mb-5">Generate a resume + cover letter + LinkedIn + DMs in ShansoResume, then hit Save.</p>
            <Link to="/ai-tools/resume-ats"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-black font-bold text-sm"
                  style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}>
              <Plus size={14} /> Start new application
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="library-grid">
            {items.map(it => (
              <div key={it.package_id}
                   className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 hover:border-cyan-500/40 transition-colors group"
                   data-testid={`library-card-${it.package_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <FileText size={16} style={{ color: ACCENT }} className="shrink-0 mt-0.5" />
                  <button onClick={() => deletePackage(it.package_id)}
                          disabled={deletingId === it.package_id}
                          className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          data-testid={`delete-${it.package_id}`}
                          title="Delete">
                    {deletingId === it.package_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </div>
                <p className="text-white text-sm font-semibold line-clamp-2 mb-1">{it.title}</p>
                {(it.company || it.role) && (
                  <p className="text-zinc-500 text-[11px] mb-2 flex items-center gap-1 truncate">
                    {it.company && <><Building2 size={10} /> <span>{it.company}</span></>}
                    {it.role && <span>{it.company ? " · " : ""}{it.role}</span>}
                  </p>
                )}
                <div className="flex flex-wrap gap-1 mb-3">
                  {ARTIFACT_META.filter(a => it.artifacts?.[a.key]).map(a => (
                    <span key={a.key} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-200" title={a.label}>
                      <a.Icon size={9} /> {a.label}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between text-[11px] text-zinc-500">
                  <span className="inline-flex items-center gap-1"><Calendar size={11} /> {new Date(it.updated_at).toLocaleDateString()}</span>
                  <button onClick={() => openPreview(it.package_id)}
                          className="text-cyan-300 hover:underline inline-flex items-center gap-1"
                          data-testid={`open-${it.package_id}`}>
                    <Eye size={11} /> Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="preview-modal">
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 p-4 border-b border-zinc-900">
              <div>
                <p className="text-white font-bold text-base">{preview.title}</p>
                <p className="text-zinc-500 text-[11px]">{preview.company || ""} {preview.role ? `· ${preview.role}` : ""} · updated {new Date(preview.updated_at).toLocaleString()}</p>
              </div>
              <button onClick={() => setPreview(null)}
                      className="text-zinc-400 hover:text-white p-1 rounded hover:bg-zinc-900"
                      data-testid="close-preview">
                <X size={18} />
              </button>
            </div>
            {/* Tab bar */}
            <div className="flex gap-1 px-4 pt-3 overflow-x-auto" data-testid="preview-tabs">
              {[
                { id: "rewrite",  label: "Rewrite",       Icon: Sparkles,       has: !!preview.rewritten_markdown },
                { id: "cover",    label: "Cover letter",  Icon: Mail,           has: !!preview.cover_letter_markdown },
                { id: "linkedin", label: "LinkedIn",      Icon: Linkedin,       has: !!preview.linkedin },
                { id: "dms",      label: "Recruiter DMs", Icon: MessageSquare,  has: !!preview.recruiter_dms },
                { id: "score",    label: "ATS score",     Icon: Target,         has: !!preview.score },
              ].filter(t => t.has).map(t => {
                const active = previewTab === t.id;
                return (
                  <button key={t.id} onClick={() => setPreviewTab(t.id)}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${active ? "border-cyan-400 text-white" : "border-transparent text-zinc-400 hover:text-white"}`}
                          data-testid={`preview-tab-${t.id}`}>
                    <t.Icon size={12} /> {t.label}
                  </button>
                );
              })}
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {previewTab === "rewrite" && preview.rewritten_markdown && (
                <div className="prose prose-invert prose-h1:text-2xl prose-h2:text-base prose-h2:text-cyan-300 prose-headings:text-white prose-p:text-zinc-300 prose-li:text-zinc-300 prose-strong:text-white max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{preview.rewritten_markdown}</ReactMarkdown>
                </div>
              )}
              {previewTab === "cover" && preview.cover_letter_markdown && (
                <div className="prose prose-invert prose-headings:text-white prose-p:text-zinc-300 prose-strong:text-white max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{preview.cover_letter_markdown}</ReactMarkdown>
                </div>
              )}
              {previewTab === "linkedin" && preview.linkedin && (
                <div className="space-y-3 text-zinc-300 text-sm">
                  <p>Overall score: <span className="text-cyan-300 font-bold">{preview.linkedin.overall_score}/100</span></p>
                  {["headline","about","experience"].map(sec => preview.linkedin[sec] && (
                    <div key={sec} className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
                      <p className="text-white text-sm font-bold capitalize mb-1">{sec} ({preview.linkedin[sec].score}/100)</p>
                      <div className="text-xs text-zinc-300"><ReactMarkdown remarkPlugins={[remarkGfm]}>{preview.linkedin[sec].rewritten}</ReactMarkdown></div>
                    </div>
                  ))}
                </div>
              )}
              {previewTab === "dms" && preview.recruiter_dms?.variants && (
                <div className="space-y-3">
                  {preview.recruiter_dms.variants.map((v, i) => (
                    <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
                      <p className="text-white text-sm font-bold">{v.label} <span className="text-zinc-500 text-[11px] font-normal">· {v.format}</span></p>
                      {v.subject && <p className="text-cyan-300 text-xs mt-1"><span className="text-zinc-500">Subject:</span> {v.subject}</p>}
                      <div className="text-xs text-zinc-300 mt-2 prose prose-invert max-w-none prose-p:text-zinc-300"><ReactMarkdown remarkPlugins={[remarkGfm]}>{v.body}</ReactMarkdown></div>
                    </div>
                  ))}
                </div>
              )}
              {previewTab === "score" && preview.score && (
                <div className="space-y-3 text-zinc-300 text-sm">
                  <p>Overall: <span className="text-cyan-300 font-bold">{preview.score.overall_score}/100</span> ({preview.score.band})</p>
                  <p className="text-zinc-500 text-xs">{preview.score.semantic?.detected_role}</p>
                  {preview.score.semantic?.weakness_flags?.map((w, i) => (
                    <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 text-xs">
                      <p className="text-white font-semibold">[{w.severity}] {w.issue}</p>
                      <p className="text-zinc-400 mt-1">Fix: {w.fix}</p>
                    </div>
                  ))}

                  {(previewRecsLoading || (previewRecs?.recommended_modules?.length > 0)) && (
                    <div className="rounded-xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-zinc-900/40 p-3 space-y-2" data-testid="preview-skill-gap-recs">
                      <div className="flex items-center gap-2">
                        <Code2 size={14} className="text-cyan-300" />
                        <p className="text-white text-xs font-bold">Close these gaps on Python Lab</p>
                      </div>
                      {previewRecsLoading && (
                        <div className="text-zinc-400 text-[11px] flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Matching gaps…</div>
                      )}
                      {!previewRecsLoading && (
                        <div className="grid sm:grid-cols-3 gap-2">
                          {previewRecs.recommended_modules.map((m, i) => (
                            <Link key={m.module_slug} to={m.deeplink}
                                  className="bg-zinc-950/60 hover:bg-zinc-900/80 border border-zinc-800 hover:border-cyan-500/50 rounded-lg p-2.5 transition-all"
                                  data-testid={`preview-pylab-rec-${i}`}>
                              <div className="flex items-start justify-between gap-1 mb-1">
                                <p className="text-white text-[12px] font-bold leading-tight">{m.module_title}</p>
                                <Rocket size={11} className="text-cyan-300 mt-0.5 shrink-0" />
                              </div>
                              <p className="text-zinc-400 text-[10px] leading-snug mb-1.5">{m.why_it_closes_gap}</p>
                              <div className="flex items-center justify-between text-[10px] text-zinc-500">
                                <span>{m.lessons_count} lessons</span>
                                <span>~{Math.round(m.est_minutes / 60)}h</span>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                      {!previewRecsLoading && previewRecs?.cta_message && (
                        <p className="text-cyan-100 text-[11px]">{previewRecs.cta_message}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {previewLoading && <div className="fixed bottom-6 right-6 bg-zinc-900 border border-zinc-800 text-zinc-300 text-sm px-3 py-2 rounded-lg flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
    </div>
  );
}
